import React, { useState, useEffect, useRef, useCallback } from "react";
import "./Eventos.css";
import axios from "axios";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";

const getBackgroundUrl = (payload) => {
  let backgroundData = payload?.data ?? payload;

  if (Array.isArray(backgroundData)) {
    backgroundData = backgroundData[0];
  }

  const candidate =
    (typeof backgroundData === "string" && backgroundData) ||
    backgroundData?.full_path ||
    backgroundData?.url ||
    backgroundData?.image ||
    backgroundData?.imagem ||
    backgroundData?.background;

  if (!candidate || typeof candidate !== "string") return "";

  try {
    const parsedUrl = new URL(candidate, window.location.origin);
    return ["http:", "https:"].includes(parsedUrl.protocol) ? parsedUrl.href : "";
  } catch (error) {
    return "";
  }
};

const Eventos = () => {
  const [events, setEvents] = useState({ data: [] });
  const [siteBackground, setSiteBackground] = useState("");
  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [background, setBackground] = useState(null);
  const [ciclista, setCiclista] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [imageToCrop, setImageToCrop] = useState("");
  const [croppedImage, setCroppedImage] = useState("");
  const [dominantColors, setDominantColors] = useState({
    primary: "rgb(0,0,0)",
    secondary: "rgb(255,255,255)",
  });

  const [fontLoaded, setFontLoaded] = useState(false);

  // Reference for the cropper
  const cropperRef = useRef(null);

  useEffect(() => {
    const loadFont = async () => {
      try {
        const font = new FontFace("MinhaFonte3", "url(../fonts/MinhaFonte3.ttf)");
        await font.load();
        document.fonts.add(font);
        setFontLoaded(true);
      } catch (error) {
        console.error("Erro ao carregar a fonte:", error);
      }
    };
    loadFont();
  }, []);

  // Função para buscar eventos da API
  const fetchEventos = async () => {
    try {
      const response = await axios.get("https://www.euvoup14sports.com.br/api/event");
      setEvents(response.data || { data: [] });
    } catch (error) {
      console.error("Erro ao buscar eventos", error);
    }
  };

  useEffect(() => {
    fetchEventos();
  }, []);

  useEffect(() => {
    const fetchBackground = async () => {
      try {
        const response = await axios.get(
          "https://www.euvoup14sports.com.br/api/background"
        );
        setSiteBackground(getBackgroundUrl(response.data));
      } catch (error) {
        // Mantém o fundo padrão definido no CSS caso a API não esteja disponível.
        setSiteBackground("");
      }
    };

    fetchBackground();
  }, []);

  // Função para obter a cor predominante de um canvas e retornar contraste
  const getDominantColors = (canvas) => {
    if (!canvas) return { primary: "rgb(0,0,0)", secondary: "rgb(255,255,255)" };

    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;
    const colorCount = {};

    for (let i = 0; i < pixels.length; i += 4) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      const a = pixels[i + 3];

      if (a < 128) continue;
      if (r > 235 && g > 235 && b > 235) continue;

      const roundedR = Math.round(r / 10) * 10;
      const roundedG = Math.round(g / 10) * 10;
      const roundedB = Math.round(b / 10) * 10;

      const key = `${roundedR},${roundedG},${roundedB}`;
      colorCount[key] = (colorCount[key] || 0) + 1;
    }

    const sorted = Object.entries(colorCount).sort((a, b) => b[1] - a[1]);
    const primary = sorted[0] ? `rgb(${sorted[0][0]})` : "rgb(0,0,0)";

    const isLight = (colorStr) => {
      const [r, g, b] = colorStr
        .replace("rgb(", "")
        .replace(")", "")
        .split(",")
        .map((n) => Number(n.trim()));
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5;
    };

    const secondary = isLight(primary) ? "rgb(35,35,35)" : "rgb(245,245,245)";

    return { primary, secondary };
  };

  // Selecionar evento e carregar a imagem do ciclista
  const handleEventoClick = (evento) => {
    setEventoSelecionado(evento);
    setErrorMessage("");

    if (evento.base64_image) {
      const ciclistaImage = `data:image/png;base64,${evento.base64_image}`;
      setCiclista(ciclistaImage);

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const colors = getDominantColors(canvas);
        setDominantColors(colors);
      };
      img.src = ciclistaImage;
    } else {
      setCiclista(null);
    }

    // Limpar fundo/preview ao abrir modal
    setBackground(null);
    setImageToCrop("");
    setCroppedImage("");
  };

  // Recebe a imagem do input (fundo)
  const handleImageToCrop = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result);
      setBackground(reader.result);
      // não precisa chamar update aqui; useEffect vai cuidar
    };
    reader.readAsDataURL(file);
  };

  // Update imagem composta (fundo + ciclista + texto)
  const updateCroppedImage = useCallback(() => {
    if (!cropperRef.current || !imageToCrop) return;

    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    try {
      const croppedCanvas = cropper.getCroppedCanvas({
        width: 1080,
        height: 1080,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high",
      });
      if (!croppedCanvas) return;

      const finalCanvas = document.createElement("canvas");
      finalCanvas.width = 1080;
      finalCanvas.height = 1080;
      const ctx = finalCanvas.getContext("2d");

      // 1) fundo
      ctx.drawImage(croppedCanvas, 0, 0, 1080, 1080);

      // função de texto
      const addTextToCanvas = () => {
        if (!eventoSelecionado) return;

        const name = eventoSelecionado.name || "";
        const city = eventoSelecionado.city || "";
        const displayText = `${name} - ${city}`.trim();
        if (!displayText) return;

        // Se quiser usar a fonte custom quando carregar:
        // ctx.font = fontLoaded ? "bold 150px MinhaFonte3" : "bold 150px Arial";
        ctx.font = "bold 150px Arial";

        ctx.fillStyle = dominantColors.secondary;
        ctx.strokeStyle = dominantColors.primary;
        ctx.lineWidth = 30;

        // escala para caber no target
        const metrics = ctx.measureText(displayText);
        const originalWidth = metrics.width || 1;
        const originalHeight =
          (metrics.actualBoundingBoxAscent || 0) + (metrics.actualBoundingBoxDescent || 0) || 1;

        const scaleX = 700 / originalWidth;
        const scaleY = 100 / originalHeight;

        ctx.save();
        ctx.translate(540, 850);
        ctx.scale(scaleX, scaleY);
        ctx.textAlign = "center";
        ctx.textBaseline = "bottom";
        ctx.strokeText(displayText, 0, 0);
        ctx.fillText(displayText, 0, 0);
        ctx.restore();
      };

      // ✅ 2) já mostra o preview do fundo IMEDIATAMENTE
      // (se o ciclista demorar ou falhar, você não fica sem preview)
      setCroppedImage(finalCanvas.toDataURL("image/png"));

      // 3) ciclista por cima (se existir) e texto no topo
      if (ciclista) {
        const ciclistaImg = new Image();
        ciclistaImg.onload = () => {
          ctx.drawImage(ciclistaImg, 0, 0, finalCanvas.width, finalCanvas.height);
          addTextToCanvas();
          setCroppedImage(finalCanvas.toDataURL("image/png"));
        };
        ciclistaImg.onerror = (e) => {
          console.error("Erro ao carregar ciclista:", e);
          // ainda assim tenta colocar o texto em cima do fundo
          addTextToCanvas();
          setCroppedImage(finalCanvas.toDataURL("image/png"));
        };
        ciclistaImg.src = ciclista;
      } else {
        addTextToCanvas();
        setCroppedImage(finalCanvas.toDataURL("image/png"));
      }
    } catch (error) {
      console.error("Erro ao atualizar imagem cortada:", error);
    }
  }, [imageToCrop, ciclista, eventoSelecionado, dominantColors, fontLoaded]);

  // ✅ Recalcula quando mudar nome/cidade, ciclista, cores ou a imagem
  useEffect(() => {
    updateCroppedImage();
  }, [updateCroppedImage]);

  // Função para converter Base64 para arquivo
  function base64ToFile(base64, filename) {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) u8arr[n] = bstr.charCodeAt(n);

    return new File([u8arr], filename, { type: mime });
  }

  // Função para enviar para o servidor
  const sendToServer = () => {
    if (!croppedImage || !eventoSelecionado?.id) {
      setErrorMessage("Crie a imagem primeiro antes de enviar!");
      return;
    }

    setIsLoading(true);

    try {
      const file = base64ToFile(croppedImage, "avatar.png");
      const dataToSend = new FormData();
      dataToSend.append("event", eventoSelecionado.id);
      dataToSend.append("name", eventoSelecionado.name || "");
      dataToSend.append("city", eventoSelecionado.city || "");
      dataToSend.append("avatar", file);

      axios
        .post("https://www.euvoup14sports.com.br/api/client", dataToSend, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          console.log("Avatar enviado com sucesso:", response.data);
          setErrorMessage("Avatar enviado com sucesso!");
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Erro ao enviar avatar para o servidor:", error);
          setErrorMessage("Erro ao salvar o avatar no servidor.");
          setIsLoading(false);
        });
    } catch (error) {
      console.error("Erro ao processar e enviar avatar:", error);
      setErrorMessage("Ocorreu um erro ao processar o avatar.");
      setIsLoading(false);
    }
  };

  // Função para baixar a imagem final
  const handleDownload = () => {
    if (!croppedImage) {
      setErrorMessage("Selecione e corte uma imagem primeiro");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = croppedImage;
      link.download = `avatar-${eventoSelecionado?.title || "evento"}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Erro ao baixar imagem:", error);
      setErrorMessage("Erro ao gerar o download da imagem");
    }

    sendToServer();
  };

  // Atualiza nome/cidade (sem chamar update aqui; useEffect cuida)
  const onSetEventoSelecionado = (newEventState) => {
    setEventoSelecionado(newEventState);
  };

  // Fechar o modal
  const closeModal = () => {
    setEventoSelecionado(null);
    setErrorMessage("");
    setCiclista(null);
    setBackground(null);
    setImageToCrop("");
    setCroppedImage("");
  };

  return (
    <div
      className="public"
      style={
        siteBackground ? { backgroundImage: `url("${siteBackground}")` } : undefined
      }
    >
      <header className="header">
        <h1 className="tinos-bold">Somos a P14 Sports</h1>
        <p className="tinos-bold">
          Experiências que conectam. Movimentos que transformam.
        </p>
      </header>

      <main className="eventos-container">
        {events?.data?.length > 0 ? (
          events.data.map((event) => (
            <button
              type="button"
              key={event.id}
              className="eventos-grid"
              onClick={() => handleEventoClick(event)}
            >
              <span className="content">
                <span className="title tinos-bold">{event.title}</span>
                <span className="date">
                  {new Date(event.date_start).toLocaleDateString() ===
                  new Date(event.date_end).toLocaleDateString()
                    ? new Date(event.date_start).toLocaleDateString()
                    : `${new Date(event.date_start).toLocaleDateString()} até ${new Date(
                        event.date_end
                      ).toLocaleDateString()}`}
                </span>
                <span className="description">
                  {event.description || "Venha participar deste evento incrível!"}
                </span>
              </span>

              <span className="evento-image">
                <img src={event.full_path} alt={`Evento: ${event.title}`} />
              </span>
            </button>
          ))
        ) : (
          <p className="events-loading">Carregando eventos...</p>
        )}

        {eventoSelecionado && (
          <div className="modal-overlay-avatar" role="presentation">
            <div
              className="modal-content-avatar"
              role="dialog"
              aria-modal="true"
              aria-labelledby="avatar-modal-title"
            >
              <form>
                <button
                  type="button"
                  className="close-button"
                  onClick={closeModal}
                  aria-label="Fechar"
                >
                  &times;
                </button>

                <div className="avatar-creator">
                  <h2 id="avatar-modal-title">
                    Insira suas informações abaixo e gere seu avatar! 🚴‍♂️
                  </h2>

                  <div className="floating-field">
                    <input
                      id="avatar-name"
                      type="text"
                      name="name"
                      placeholder=" "
                      value={eventoSelecionado.name || ""}
                      onChange={(e) =>
                        onSetEventoSelecionado({
                          ...eventoSelecionado,
                          name: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor="avatar-name">Nome</label>
                  </div>

                  <div className="floating-field">
                    <input
                      id="avatar-city"
                      name="city"
                      type="text"
                      placeholder=" "
                      value={eventoSelecionado.city || ""}
                      onChange={(e) =>
                        onSetEventoSelecionado({
                          ...eventoSelecionado,
                          city: e.target.value,
                        })
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <label htmlFor="avatar-city">Cidade</label>
                  </div>

                  <div className="upload-section">
                    <label htmlFor="bg-upload" className="upload-label">
                      <h2>Selecione sua foto</h2>
                      <input type="file" accept="image/*" onChange={handleImageToCrop} />
                    </label>
                    {background && <p className="file-selected">✓ Imagem selecionada</p>}
                  </div>

                  {errorMessage && <div className="error-message">{errorMessage}</div>}

                  <div className="image-preview-container">
                    {imageToCrop && (
                      <div className="background-preview">
                        <Cropper
                          src={imageToCrop}
                          style={{ height: 300, width: 300 }}
                          initialAspectRatio={1}
                          guides={false}
                          ref={cropperRef}
                          scalable={false}
                          movable={true}
                          background={false}
                          viewMode={1}
                          dragMode="move"
                          // pode deixar: ele só força update enquanto move
                          crop={updateCroppedImage}
                        />

                        {croppedImage && (
                          <div className="background-preview">
                            <img
                              style={{
                                width: "300px",
                                height: "300px",
                                objectFit: "cover",
                                objectPosition: "center",
                              }}
                              className="cropped-preview"
                              src={croppedImage}
                              alt="Preview Cortado"
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="button-group">
                    <button
                      type="button"
                      onClick={handleDownload}
                      className="action-button export"
                      disabled={!croppedImage || isLoading}
                    >
                      {isLoading ? "Enviando..." : "Baixar Imagem"}
                    </button>
                  </div>

                  {/* só pra debug opcional:
                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                    Fonte carregada: {String(fontLoaded)}
                  </div>
                  */}
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Eventos;
